import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import Lottie from 'lottie-react';

import { Flex, ButtonFilled, Divider, SvgIcon, Box, Image } from 'Components/Base';

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 800,
    borderRadius: 16,
    overflowY: 'auto',
  },
  content: {
    padding: 24,
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
    // Hide scrollbars for Webkit browsers
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    // Hide scrollbars for Firefox
    scrollbarWidth: 'none',
  },
  actions: {
    padding: 16,
  },
}));

const StyledDialogContent = styled(DialogContent)`
  max-height: 400px;
  background-color: ${({ theme }) => theme.colors.neutralGreyLighter};

  @media (max-width: 768px) {
    max-height: 600px;
  }

  & > div {
    height: 100%;
  }
`;

const StyledDivider = styled(Divider)`
  border-color: ${({ theme }) => theme.colors.neutralGreyLight};
  border-width: 1px;
  border-style: solid;
  margin: 0;
`;

const ContentWrapper = styled(Flex)`
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
    flex-direction: column;
  }

  & svg {
    border-radius: 16px;
  }
`;

const isLottieAnimation = (visual) =>
  typeof visual === 'object' && visual !== null && 'v' in visual;

const isImageWithSrc = (visual) => typeof visual === 'object' && visual !== null && 'src' in visual;

const Content = ({ content, ...props }) => {
  const { visual } = content;

  return (
    <ContentWrapper {...props}>
      <Flex borderRadius={16} flex={1} maxHeight={350}>
        {visual && isLottieAnimation(visual) && (
          <Lottie style={{ width: '100%', height: '100%' }} animationData={visual} loop={true} />
        )}
        {visual && isImageWithSrc(visual) && (
          <Image
            style={{ width: '100%', height: '100%' }}
            src={visual.src}
            srcSet={visual.srcSet}
            alt={'presentation-modal-content'}
          />
        )}
        {visual && typeof visual === 'string' && (
          <Image
            style={{ width: '100%', height: '100%' }}
            src={visual}
            alt={'presentation-modal-content'}
          />
        )}
      </Flex>
      <Flex flexDirection={'column'} py={{ xs: 0, sm: 6 }} flex={1}>
        {content.content}
      </Flex>
    </ContentWrapper>
  );
};

Content.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.node,
    visual: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string,
      }),
      PropTypes.object, // lottie json
    ]),
  }),
};

const FooterWrapper = styled(Flex)`
  width: 100%;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;

const Dot = styled(Box)`
  width: 16px;
  height: 16px;
  border-radius: 50%;
`;

const DotWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-around;
  max-width: 112px;
  width: 100%;
`;

const Footer = ({ primaryAction, secondaryAction, contentIndex, contentLength }) => (
  <FooterWrapper>
    <DotWrapper>
      {Array.from({ length: contentLength }, (_, index) => (
        <Dot key={index} bg={contentIndex === index ? 'primary' : 'greyscale.grey4'} />
      ))}
    </DotWrapper>
    <Flex style={{ gap: 16 }}>
      {!secondaryAction.hide && (
        <ButtonFilled
          color={'greyscale.grey4'}
          width={{ xs: '56px', sm: 'auto' }}
          minWidth={0}
          height={48}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          onClick={secondaryAction.onClick}
        >
          <SvgIcon icon={SvgIcon.ICON_LIST.ARROW_LEFT} size={24} color="iconColorSecondary" />
        </ButtonFilled>
      )}
      <ButtonFilled
        minWidth={{ xs: '84px', sm: '98px' }}
        width={{ xs: 'auto', sm: '98px' }}
        height={48}
        onClick={primaryAction.onClick}
      >
        {primaryAction.label}
      </ButtonFilled>
    </Flex>
  </FooterWrapper>
);

Footer.propTypes = {
  contentIndex: PropTypes.number,
  contentLength: PropTypes.number,
  primaryAction: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  secondaryAction: PropTypes.shape({
    onClick: PropTypes.func,
    hide: PropTypes.bool,
  }),
};

const PresentationModal = ({ title, open, contents, onClose }) => {
  const [contentIndex, setContentIndex] = React.useState(0);
  const contentRef = React.useRef(null);
  const classes = useStyles();

  const next = () => {
    if (contentIndex < contents.length - 1) {
      setContentIndex(contentIndex + 1);
    } else {
      onClose();
    }
  };

  const prev = () => {
    if (contentIndex > 0) {
      setContentIndex(contentIndex - 1);
    }
  };

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [contentIndex]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      disableBackdropClick
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography>{title}</DialogTitle>
      <StyledDivider />
      <StyledDialogContent ref={contentRef} classes={{ root: classes.content }}>
        {contents.map((content, index) => (
          <Content
            key={index}
            content={content}
            style={{ display: index === contentIndex ? 'flex' : 'none' }}
          />
        ))}
      </StyledDialogContent>
      <StyledDivider />
      <DialogActions classes={{ root: classes.actions }}>
        <Footer
          primaryAction={{ label: contents[contentIndex].buttonText, onClick: next }}
          secondaryAction={{
            onClick: prev,
            hide: contentIndex === 0,
          }}
          contentIndex={contentIndex}
          contentLength={contents.length}
        />
      </DialogActions>
    </Dialog>
  );
};

PresentationModal.propTypes = {
  title: PropTypes.node,
  contents: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node,
      visual: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          src: PropTypes.string.isRequired,
          srcSet: PropTypes.string,
        }),
        PropTypes.object, // lottie
      ]),
      buttonText: PropTypes.string,
    })
  ),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

PresentationModal.defaultProps = {
  title: '',
  contents: [],
  onClose: () => {},
  open: false,
};

export default PresentationModal;
